import { useMemo, useState } from "react";
import {
  AlertCircle,
  CheckCircle2,
  Clock3,
  FileText,
  Gavel,
  MessageSquare,
  ShieldAlert,
  Trophy,
  UserRound,
  Eye,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import { useNavigate } from "react-router-dom";

import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";

import { getErrorMessage } from "@/utils/getErrorMessage";
import { useDisputes } from "@/hooks";
import type { Dispute } from "@/interfaces/dispute";

type DisputeOutcome = "BUYER_WON" | "SELLER_WON";

type DisputeWithResolution = Dispute & {
  outcome?: DisputeOutcome | null;
  resolution?: string | null;
  resolvedAt?: string | null;

  resolvedBy?: {
    id: string;
    firstName?: string | null;
    lastName?: string | null;
    email?: string | null;
  } | null;

  resolvedById?: string | null;

  trade?: {
    buyer?: {
      id: string;
      firstName?: string | null;
      lastName?: string | null;
      email?: string | null;
    } | null;

    seller?: {
      id: string;
      firstName?: string | null;
      lastName?: string | null;
      email?: string | null;
    } | null;
  } | null;
};

type PartyRole = "buyer" | "seller";

const formatDate = (value?: string | null) => {
  if (!value) {
    return "—";
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return "—";
  }

  return new Intl.DateTimeFormat("en-US", {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(date);
};

const getFullName = (
  user?: {
    firstName?: string | null;
    lastName?: string | null;
    email?: string | null;
  } | null,
) => {
  if (!user) {
    return "Unknown user";
  }

  const name = [user.firstName, user.lastName].filter(Boolean).join(" ").trim();

  return name || user.email || "Unknown user";
};

const getInitials = (
  user?: {
    firstName?: string | null;
    lastName?: string | null;
    email?: string | null;
  } | null,
) => {
  if (!user) {
    return "U";
  }

  const name = [user.firstName, user.lastName].filter(Boolean).join(" ").trim();

  if (name) {
    return name
      .split(/\s+/)
      .slice(0, 2)
      .map((part) => part.charAt(0).toUpperCase())
      .join("");
  }

  return user.email?.charAt(0).toUpperCase() || "U";
};

const getWinnerLabel = (dispute: DisputeWithResolution) => {
  if (dispute.outcome === "BUYER_WON") {
    return "Buyer";
  }

  if (dispute.outcome === "SELLER_WON") {
    return "Seller";
  }

  return null;
};

const getWinnerRole = (dispute: DisputeWithResolution): PartyRole | null => {
  if (dispute.outcome === "BUYER_WON") {
    return "buyer";
  }

  if (dispute.outcome === "SELLER_WON") {
    return "seller";
  }

  return null;
};

const getComplainantRole = (
  dispute: DisputeWithResolution,
): PartyRole | null => {
  const buyerId = dispute.trade?.buyer?.id;
  const sellerId = dispute.trade?.seller?.id;
  const openedById = dispute.openedBy?.id;

  if (openedById && openedById === buyerId) {
    return "buyer";
  }

  if (openedById && openedById === sellerId) {
    return "seller";
  }

  return null;
};

const getStatusBadge = (status?: string | null) => {
  switch (status) {
    case "RESOLVED":
      return (
        <Badge className="border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-50 dark:border-emerald-900/40 dark:bg-emerald-950/30 dark:text-emerald-400">
          <CheckCircle2 className="mr-1.5 h-3 w-3" />
          Resolved
        </Badge>
      );

    case "OPEN":
      return (
        <Badge className="border-amber-200 bg-amber-50 text-amber-700 hover:bg-amber-50 dark:border-amber-900/40 dark:bg-amber-950/30 dark:text-amber-400">
          <Clock3 className="mr-1.5 h-3 w-3" />
          Open
        </Badge>
      );

    case "PENDING":
      return (
        <Badge className="border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-50 dark:border-blue-900/40 dark:bg-blue-950/30 dark:text-blue-400">
          <Clock3 className="mr-1.5 h-3 w-3" />
          Pending
        </Badge>
      );

    case "CLOSED":
      return <Badge variant="secondary">Closed</Badge>;

    default:
      return <Badge variant="secondary">{status || "Unknown"}</Badge>;
  }
};

const AdminDisputes = () => {
  const navigate = useNavigate();

  const [page, setPage] = useState(1);
  const limit = 10;

  const { data, isLoading, isFetching, isError, error } = useDisputes({
    page,
    limit,
  });

  const disputes = useMemo(
    () => (data?.data ?? []) as DisputeWithResolution[],
    [data],
  );

  const meta = data?.meta;

  console.log("disputes", disputes);

  const resolvedDisputes = disputes.filter(
    (dispute) => dispute.status === "RESOLVED",
  ).length;

  const openDisputes = disputes.filter(
    (dispute) => dispute.status !== "RESOLVED",
  ).length;

  const resolutionRate =
    disputes.length > 0
      ? Math.round((resolvedDisputes / disputes.length) * 100)
      : 0;

  const handleViewDetails = (id: string) => {
    navigate(`/admin/disputes/${id}`);
  };

  if (isLoading) {
    return (
      <div className="flex min-h-[500px] items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
          <p className="text-sm text-muted-foreground">Loading disputes...</p>
        </div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="p-4 sm:p-6">
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Failed to load disputes</AlertTitle>
          <AlertDescription>
            {getErrorMessage(error) ||
              "Something went wrong while loading disputes."}
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="min-h-full space-y-6 p-4 sm:p-6 lg:p-8">
      {/* HEADER */}
      <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary/10">
              <ShieldAlert className="h-5 w-5 text-primary" />
            </div>

            <div>
              <h1 className="text-xl font-bold tracking-tight sm:text-2xl">
                Dispute Management
              </h1>

              <p className="mt-0.5 text-sm text-muted-foreground">
                Review, investigate, and resolve marketplace disputes.
              </p>
            </div>
          </div>
        </div>

        {isFetching && (
          <div className="flex items-center gap-2 self-start rounded-lg border bg-background px-3 py-2 text-xs text-muted-foreground lg:self-auto">
            <span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-primary border-t-transparent" />
            Updating disputes...
          </div>
        )}
      </div>

      {/* SUMMARY */}
      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <Card className="shadow-sm">
          <CardContent className="p-5">
            <div className="flex items-center justify-between gap-4">
              <div>
                <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                  Total disputes
                </p>

                <p className="mt-1 text-2xl font-bold">
                  {meta?.total ?? disputes.length}
                </p>

                <p className="mt-1 text-xs text-muted-foreground">
                  All submitted disputes
                </p>
              </div>

              <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-primary/10">
                <Gavel className="h-5 w-5 text-primary" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="shadow-sm">
          <CardContent className="p-5">
            <div className="flex items-center justify-between gap-4">
              <div>
                <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                  Open
                </p>

                <p className="mt-1 text-2xl font-bold">{openDisputes}</p>

                <p className="mt-1 text-xs text-muted-foreground">
                  Require administrative review
                </p>
              </div>

              <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-amber-100 dark:bg-amber-950/30">
                <Clock3 className="h-5 w-5 text-amber-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="shadow-sm">
          <CardContent className="p-5">
            <div className="flex items-center justify-between gap-4">
              <div>
                <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                  Resolved
                </p>

                <p className="mt-1 text-2xl font-bold">{resolvedDisputes}</p>

                <p className="mt-1 text-xs text-muted-foreground">
                  Current page
                </p>
              </div>

              <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-emerald-100 dark:bg-emerald-950/30">
                <CheckCircle2 className="h-5 w-5 text-emerald-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="shadow-sm">
          <CardContent className="p-5">
            <div className="flex items-center justify-between gap-4">
              <div>
                <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                  Resolution rate
                </p>

                <p className="mt-1 text-2xl font-bold">{resolutionRate}%</p>

                <p className="mt-1 text-xs text-muted-foreground">
                  Current page
                </p>
              </div>

              <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-blue-100 dark:bg-blue-950/30">
                <Trophy className="h-5 w-5 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* EMPTY */}
      {disputes.length === 0 ? (
        <Card className="shadow-sm">
          <CardContent className="flex min-h-[360px] flex-col items-center justify-center px-6 text-center">
            <div className="flex h-14 w-14 items-center justify-center rounded-full bg-muted">
              <CheckCircle2 className="h-7 w-7 text-muted-foreground" />
            </div>

            <h3 className="mt-4 text-lg font-semibold">No disputes found</h3>

            <p className="mt-1 max-w-md text-sm text-muted-foreground">
              There are currently no disputes requiring administrative review.
            </p>
          </CardContent>
        </Card>
      ) : (
        <>
          {/* ============================================================ */}
          {/* DESKTOP TABLE                                                */}
          {/* ============================================================ */}

          <Card className="hidden overflow-hidden shadow-sm lg:block">
            <CardHeader className="border-b bg-muted/20 px-5 py-4">
              <div className="flex items-center justify-between gap-4">
                <div>
                  <CardTitle className="text-base">Dispute Queue</CardTitle>

                  <p className="mt-1 text-xs text-muted-foreground">
                    Review disputes and inspect supporting evidence.
                  </p>
                </div>

                <Badge variant="outline">{disputes.length} displayed</Badge>
              </div>
            </CardHeader>

            <CardContent className="p-0">
              <div className="w-full overflow-x-auto">
                <table className="w-full min-w-[1050px]">
                  <thead>
                    <tr className="border-b bg-muted/10">
                      <th className="px-5 py-3 text-left text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                        Dispute
                      </th>

                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                        Parties
                      </th>

                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                        Complainant
                      </th>

                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                        Status
                      </th>

                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                        Evidence
                      </th>

                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                        Created
                      </th>

                      <th className="px-5 py-3 text-right text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                        Action
                      </th>
                    </tr>
                  </thead>

                  <tbody className="divide-y">
                    {disputes.map((dispute) => {
                      const winner = getWinnerLabel(dispute);
                      const winnerRole = getWinnerRole(dispute);
                      const complainantRole = getComplainantRole(dispute);

                      const buyer = dispute.trade?.buyer;
                      const seller = dispute.trade?.seller;

                      const evidenceCount = Array.isArray(dispute.images)
                        ? dispute.images.length
                        : 0;

                      return (
                        <tr
                          key={dispute.id}
                          className="group transition-colors hover:bg-muted/20"
                        >
                          {/* DISPUTE */}
                          <td className="px-5 py-4 align-top">
                            <div className="max-w-[250px]">
                              <p className="break-all text-sm font-semibold">
                                #{dispute.id}
                              </p>

                              <p className="mt-1 truncate text-xs text-muted-foreground">
                                Trade #{dispute.tradeId}
                              </p>

                              <p className="mt-2 line-clamp-2 text-xs text-muted-foreground">
                                {dispute.reason || "No reason provided"}
                              </p>
                            </div>
                          </td>

                          {/* PARTIES */}
                          <td className="px-4 py-4 align-top">
                            <div className="space-y-2">
                              <div className="flex items-center gap-2">
                                <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-muted text-[10px] font-semibold">
                                  {getInitials(buyer)}
                                </div>

                                <div className="min-w-0">
                                  <p className="truncate text-xs font-medium">
                                    {getFullName(buyer)}
                                  </p>

                                  <p className="text-[10px] text-muted-foreground">
                                    Buyer
                                  </p>
                                </div>

                                {winnerRole === "buyer" && (
                                  <Trophy className="ml-auto h-3.5 w-3.5 shrink-0 text-emerald-600" />
                                )}
                              </div>

                              <div className="flex items-center gap-2">
                                <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-muted text-[10px] font-semibold">
                                  {getInitials(seller)}
                                </div>

                                <div className="min-w-0">
                                  <p className="truncate text-xs font-medium">
                                    {getFullName(seller)}
                                  </p>

                                  <p className="text-[10px] text-muted-foreground">
                                    Seller
                                  </p>
                                </div>

                                {winnerRole === "seller" && (
                                  <Trophy className="ml-auto h-3.5 w-3.5 shrink-0 text-emerald-600" />
                                )}
                              </div>
                            </div>
                          </td>

                          {/* COMPLAINANT */}
                          <td className="px-4 py-4 align-top">
                            <div className="flex items-center gap-2">
                              <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-muted">
                                <UserRound className="h-4 w-4 text-muted-foreground" />
                              </div>

                              <div className="min-w-0">
                                <p className="truncate text-xs font-semibold">
                                  {getFullName(dispute.openedBy)}
                                </p>

                                <p className="truncate text-[10px] text-muted-foreground">
                                  {complainantRole
                                    ? complainantRole === "buyer"
                                      ? "Buyer"
                                      : "Seller"
                                    : "Participant"}
                                </p>
                              </div>
                            </div>
                          </td>

                          {/* STATUS */}
                          <td className="px-4 py-4 align-top">
                            <div className="space-y-2">
                              {getStatusBadge(dispute.status)}

                              {winner && (
                                <div className="flex items-center gap-1 text-[11px] font-medium text-emerald-600">
                                  <Trophy className="h-3 w-3" />
                                  {winner} won
                                </div>
                              )}
                            </div>
                          </td>

                          {/* EVIDENCE */}
                          <td className="px-4 py-4 align-top">
                            <div className="flex items-center gap-2">
                              <FileText className="h-4 w-4 text-muted-foreground" />

                              <span className="text-sm font-medium">
                                {evidenceCount}
                              </span>

                              <span className="text-xs text-muted-foreground">
                                file{evidenceCount === 1 ? "" : "s"}
                              </span>
                            </div>
                          </td>

                          {/* CREATED */}
                          <td className="px-4 py-4 align-top">
                            <p className="whitespace-nowrap text-xs font-medium">
                              {formatDate(dispute.createdAt)}
                            </p>
                          </td>

                          {/* ACTION */}
                          <td className="px-5 py-4 text-right align-top">
                            <Button
                              variant="outline"
                              size="sm"
                              className="opacity-90 transition-opacity group-hover:opacity-100"
                              onClick={() => handleViewDetails(dispute.id)}
                            >
                              <Eye className="mr-2 h-4 w-4" />
                              Review
                            </Button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>

          {/* ============================================================ */}
          {/* MOBILE / TABLET CARDS                                        */}
          {/* ============================================================ */}

          <div className="grid gap-4 lg:hidden">
            {disputes.map((dispute) => {
              const winner = getWinnerLabel(dispute);
              const complainantRole = getComplainantRole(dispute);

              const buyer = dispute.trade?.buyer;
              const seller = dispute.trade?.seller;

              const evidenceCount = Array.isArray(dispute.images)
                ? dispute.images.length
                : 0;

              return (
                <Card key={dispute.id} className="overflow-hidden shadow-sm">
                  <CardHeader className="border-b bg-muted/20 px-4 py-4 sm:px-5">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="flex flex-wrap items-center gap-2">
                          {getStatusBadge(dispute.status)}

                          {winner && (
                            <Badge
                              variant="outline"
                              className="border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-900/40 dark:bg-emerald-950/30 dark:text-emerald-400"
                            >
                              <Trophy className="mr-1 h-3 w-3" />
                              {winner} won
                            </Badge>
                          )}
                        </div>

                        <p className="mt-3 break-all text-sm font-bold">
                          Dispute #{dispute.id}
                        </p>

                        <p className="mt-1 text-xs text-muted-foreground">
                          Trade #{dispute.tradeId}
                        </p>
                      </div>

                      <Button
                        variant="outline"
                        size="icon"
                        className="shrink-0"
                        onClick={() => handleViewDetails(dispute.id)}
                        aria-label="View dispute"
                      >
                        <Eye className="h-4 w-4" />
                      </Button>
                    </div>
                  </CardHeader>

                  <CardContent className="space-y-4 px-4 py-4 sm:px-5">
                    {/* PARTIES */}
                    <div className="grid gap-3 sm:grid-cols-2">
                      <div className="rounded-xl border p-3">
                        <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                          Buyer
                        </p>

                        <div className="mt-2 flex items-center gap-2">
                          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-muted text-xs font-semibold">
                            {getInitials(buyer)}
                          </div>

                          <div className="min-w-0">
                            <p className="truncate text-xs font-semibold">
                              {getFullName(buyer)}
                            </p>

                            <p className="truncate text-[10px] text-muted-foreground">
                              {buyer?.email || "—"}
                            </p>
                          </div>
                        </div>
                      </div>

                      <div className="rounded-xl border p-3">
                        <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                          Seller
                        </p>

                        <div className="mt-2 flex items-center gap-2">
                          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-muted text-xs font-semibold">
                            {getInitials(seller)}
                          </div>

                          <div className="min-w-0">
                            <p className="truncate text-xs font-semibold">
                              {getFullName(seller)}
                            </p>

                            <p className="truncate text-[10px] text-muted-foreground">
                              {seller?.email || "—"}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* REASON */}
                    <div>
                      <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                        Reason
                      </p>

                      <p className="mt-1 line-clamp-3 text-sm leading-6">
                        {dispute.reason || "No reason provided."}
                      </p>
                    </div>

                    <Separator />

                    {/* META */}
                    <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
                      <div>
                        <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                          Complainant
                        </p>

                        <p className="mt-1 truncate text-xs font-medium">
                          {getFullName(dispute.openedBy)}
                        </p>

                        <p className="text-[10px] text-muted-foreground">
                          {complainantRole
                            ? complainantRole === "buyer"
                              ? "Buyer"
                              : "Seller"
                            : "Participant"}
                        </p>
                      </div>

                      <div>
                        <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                          Evidence
                        </p>

                        <p className="mt-1 text-xs font-medium">
                          {evidenceCount} file
                          {evidenceCount === 1 ? "" : "s"}
                        </p>
                      </div>

                      <div className="col-span-2 sm:col-span-1">
                        <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                          Created
                        </p>

                        <p className="mt-1 text-xs font-medium">
                          {formatDate(dispute.createdAt)}
                        </p>
                      </div>
                    </div>

                    {/* ACTION */}
                    <Button
                      className="w-full"
                      onClick={() => handleViewDetails(dispute.id)}
                    >
                      <Gavel className="mr-2 h-4 w-4" />
                      Review Dispute
                    </Button>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </>
      )}

      {/* PAGINATION */}
      {meta && meta.totalPages > 1 && (
        <div className="flex flex-col gap-3 border-t pt-5 sm:flex-row sm:items-center sm:justify-between">
          <div className="text-sm text-muted-foreground">
            Page{" "}
            <span className="font-medium text-foreground">{meta.page}</span> of{" "}
            <span className="font-medium text-foreground">
              {meta.totalPages}
            </span>
          </div>

          <div className="flex w-full gap-2 sm:w-auto">
            <Button
              variant="outline"
              size="sm"
              className="flex-1 sm:flex-none"
              disabled={!meta.hasPrevPage || isFetching}
              onClick={() => setPage((current) => Math.max(1, current - 1))}
            >
              <ChevronLeft className="mr-1.5 h-4 w-4" />
              Previous
            </Button>

            <Button
              variant="outline"
              size="sm"
              className="flex-1 sm:flex-none"
              disabled={!meta.hasNextPage || isFetching}
              onClick={() => setPage((current) => current + 1)}
            >
              Next
              <ChevronRight className="ml-1.5 h-4 w-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDisputes;
